import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Container, Flex, Text, TextInput } from "@mantine/core";
import { MantineReactTable } from "mantine-react-table";
import AppBreadcrumb from "../../../components/AppBreadcrumb";
import { coupons_api } from "./coupons.service";
import { showSuccessToast, showErrorToast } from "utilities/Toast";

export default function ReferralCoupon() {
  const [data, setData] = useState([]);
  const [viewMode, setViewMode] = useState("REFERRAL"); // REFERRAL | WELCOME
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [searchedUserName, setSearchedUserName] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({
    referrer_phone: false,
  });

  useEffect(() => {
    setColumnVisibility({
      referrer_phone: viewMode === "WELCOME",
    });
  }, [viewMode]);
  


  // =========================
  // FETCH COUPONS (SINGLE API)
  // =========================
 const fetchCoupons = async (mode, uid = "", phoneParam = "") => {
  try {
    setLoading(true);
    setSearchedUserName("");

    const params = {};
    if (mode === "REFERRAL") params.source = "referral";
    if (mode === "WELCOME") params.source = "welcome";
    if (uid) params.user_id = uid;
    if (phoneParam) params.phone = phoneParam;

    const res = await coupons_api.referralCouponsGet(params);

    setData(res?.data || []);

    if (phoneParam) {
      if (res?.user_found) {
        setSearchedUserName(res.searched_user_name);
        showSuccessToast({
          title: "Success",
          message: "User found successfully",
        });
      } else {
        showErrorToast({
          title: "Error",
          message: "No user found for this number",
        });
      }
    }
  } catch (err) {
    setData([]);
    showErrorToast({
      title: "Error",
      message: "Something went wrong",
    });
  } finally {
    setLoading(false);
  }
};




  // =========================
  // DEFAULT LOAD → REFERRAL
  // =========================
  useEffect(() => {
    fetchCoupons("REFERRAL");
  }, []);

  // =========================
  // TABLE COLUMNS
  // =========================
  const columns = useMemo(
    () => [
      {
        header: "User",
        accessorKey: "user_name",
        size:80,
      },
      {
        header: "Referrer Ph.No",
        accessorKey: "referrer_phone",
        size:100,
        Cell: ({ cell }) => (
          <Text>{cell.getValue() || "-"}</Text>
        ),
      },
      {
        header: "Coupon Code",
        accessorKey: "code",
        size:100,
        Cell: ({ cell }) => (
          <Text fw={600}>{cell.getValue() || "-"}</Text>
        ),
      },
      {
        header: "Discount",
        accessorKey: "discount_amount",
        size:100,
      },
      {
        header: "Min Purchase",
        accessorKey: "min_purchase",
        size:100,
      },
      {
        header: "Expiry Date",
        accessorKey: "expiry_date",
        size:100,
      },
      {
        header: "Status",
        accessorKey: "used",
        size:100,
        Cell: ({ cell }) => {
            const used = cell.getValue(); // value of "used"
            const expiry = new Date(cell.row.original.expiry_date);
            const today = new Date();

            if (expiry < today) {
              return (
                
                <Text c="gray" fw={600}>
                  Expired
                </Text>

              );
            }

            return (
              <Text c={used ? "red" : "green"} fw={600}>
                {used ? "Used" : "Active"}
              </Text>
            );
          },
      },
      {
        header: "Source",
        accessorKey: "source",
        size:100,
        
          },
    ],
    []
  );

  // =========================
  // UI
  // =========================
  return (
    <Container fluid>

      {/* HEADER */}
      <Box px={25} py={15}>
        <Flex justify="space-between" align="end">
          <Box mt={13}>
            <Text size="lg" fw={600}>
              Referral Coupons
            </Text>
            <Box mt={6}>
              <AppBreadcrumb />
            </Box>
          </Box>

          <Flex gap="sm">
            {viewMode === "WELCOME" && (
  <Flex gap="sm">
    <TextInput
      placeholder="Search by Phone Number"
      value={phone}
      onChange={(e) => setPhone(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          fetchCoupons("WELCOME", "", phone.trim());
        }
      }}
    />

    <Button
      onClick={() => {
        fetchCoupons("WELCOME", "", phone.trim());
      }}
    >
      Search
    </Button>
  </Flex>
)}



            {viewMode === "REFERRAL" ? (
              <Button
                onClick={() => {
                  setViewMode("WELCOME");
                  setUserId("");
                  setPhone("");
                  fetchCoupons("WELCOME");
                }}
              >
                Welcome Coupons
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => {
                  setViewMode("REFERRAL");
                  setUserId("");
                  setPhone("");
                  fetchCoupons("REFERRAL");
                }}
              >
                Back
              </Button>
            )}
          </Flex>
        </Flex>
      </Box>

      {/* TABLE */}
      <Box px={25} mt={12}>
        <Box
          sx={{
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          <MantineReactTable
            columns={columns}
            data={data}
            state={{ isLoading: loading, columnVisibility }}
            onColumnVisibilityChange={setColumnVisibility}
            enableColumnResizing
            columnResizeMode="onChange"
            layoutMode="grid"
            enableColumnActions={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableGlobalFilter={false}
            initialState={{ density: "xs" }}
            renderTopToolbarCustomActions={() => (
              <Box px={24} py={14}>
                <Text fw={600} color="#266f31" tt="uppercase" size="sm">
                  {viewMode === "REFERRAL" ? (
                    "Referrer Coupon List"
                  ) : (
                    <>
                      Referral Coupon List
                      {searchedUserName && (
                        <Text span ml={8} fw={600} c="green">
                          – {searchedUserName}
                        </Text>
                      )}
                    </>
                  )}
                </Text>
              </Box>
            )}

          />
        </Box>
      </Box>
    </Container>
  );
}
